// InstallableModalStub — Phase 3 placeholder modal.
//
// Mounted at each consuming app root alongside the existing Modal overlay.
// Opens whenever root.state._installableModal is set (by openInstallableModal).
// Closing clears that key.
//
// Phase 4 (MarketplaceInstallModal extensibility) replaces this component
// with the real install/manage flow. The stateKey '_installableModal' and the
// close pattern are the stable contract Phase 4 hooks into.
//
// Mount at app root (e.g. canvas/app.js, intranet/app.js):
//   InstallableModalStub: {}
//
// No additional props needed — reads everything from root.state._installableModal.

export const InstallableModalStub = {
  extends: 'Modal',
  stateKey: '_installableModal',

  Window: {
    extends: 'ModalWindow',
    width: 'H2',
    maxWidth: '90vw',

    // Its header is custom (TitleGroup > Title), so the dialog is named by
    // that h2 (DESIGN-WL-SHARED-MODAL-DIALOG-HAS-NO-ACCESSIBLE-NAME-1).
    ariaLabelledby: 'InstallableModalStubTitle',

    ModalHeader: {
      flow: 'x',
      gap: 'Z',
      align: 'center space-between',
      padding: '0 0 Z 0',

      TitleGroup: {
        flow: 'y',
        gap: 'X2',

        Title: {
          tag: 'h2',
          id: 'InstallableModalStubTitle',
          margin: '0',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => {
            const data = s.root?._installableModal
            if (!data) return el.call('polyglot', 'apps.installModal.install')
            const label = data.kind || data.domain || ''
            return el
              .call(
                'polyglot',
                data.installed ? 'apps.installModal.manageItem' : 'apps.installModal.installItem'
              )
              .replace('{item}', label)
          }
        },

        Subtitle: {
          tag: 'p',
          margin: '0',
          fontSize: 'Z2',
          fontWeight: '400',
          color: 'caption',
          text: (el, s) => {
            const data = s.root?._installableModal
            if (!data) return ''
            const parts = []
            if (data.domain) {
              parts.push(
                el.call('polyglot', 'apps.installModal.domain').replace('{domain}', data.domain)
              )
            }
            if (data.defaultScope?.type) {
              parts.push(
                el
                  .call('polyglot', 'apps.installModal.scope')
                  .replace('{scope}', data.defaultScope.type)
              )
            }
            return parts.join(' · ')
          }
        }
      },

      // THE house close X (brand CloseButton — F2, one X for every panel).
      CloseButton: {
        extends: 'CloseButton',
        onClick: (e, el, s) => {
          s.root?.update({ _installableModal: null })
        }
      }
    },

    // ── Phase 4 notice ────────────────────────────────────────────────────

    PhaseNotice: {
      flow: 'y',
      gap: 'Z',
      padding: 'Z A',
      round: 'radiusCard',
      background: 'blue.1',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'blue.3',

      NoticeRow: {
        flow: 'x',
        gap: 'Y',
        align: 'center left',

        Icon: {
          name: 'info',
          fontSize: 'Z2',
          color: 'blue'
        },

        Label: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'blue',
          text: '{{ apps.installStub.comingSoon | polyglot }}'
        }
      },

      Description: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y2',
        color: 'paragraph',
        lineHeight: '1.5',
        text: (el, s) => {
          const data = s.root?._installableModal
          if (!data) return ''
          return (
            `openInstallableModal was called with: domain="${data.domain || '—'}", ` +
            `kind="${data.kind || '—'}", installed=${!!data.installed}. ` +
            'Phase 4 MarketplaceInstallModal extensibility will wire this up with the ' +
            'full install / manage flow.'
          )
        }
      }
    },

    // ── Args debug display ────────────────────────────────────────────────

    ArgsDisplay: {
      flow: 'y',
      gap: 'Y',
      padding: 'Z A',
      round: 'radiusCard',
      background: 'overlayStrong',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      borderColor: 'line',

      ArgsLabel: {
        tag: 'span',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '0.04em',
        color: 'caption',
        text: '{{ apps.installStub.receivedArgs | polyglot }}'
      },

      ArgsCode: {
        tag: 'pre',
        margin: '0',
        fontSize: 'Y2',
        color: 'paragraph',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        text: (el, s) => {
          const data = s.root?._installableModal
          return data ? JSON.stringify(data, null, 2) : '{}'
        }
      }
    },

    // ── Footer ────────────────────────────────────────────────────────────

    ModalFooter: {
      extends: 'ModalFooter',
      flow: 'x',
      gap: 'Y',
      align: 'center right',

      DismissButton: {
        tag: 'button',
        type: 'button',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        background: 'overlayStrong',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: '{{ apps.installStub.dismiss | polyglot }}',
        // Was `background: 'line'` — `line` is a stroke/hairline token
        // (['gray13','gray5'], meant for 1px dividers), not a state fill;
        // used as a full button hover fill it reads as a flat, out-of-
        // character gray block instead of an interaction wash. Same defect
        // class as `overlayStrong`-as-state (DESIGN_LANGUAGE §4.3/§8.1),
        // just a different token — `hover` (['black.05','white.07']) is the
        // one actually named for this role. Found while watching for this
        // exact class during LMC-4 (2026-08-10).
        ':hover': { background: 'hover' },
        onClick: (e, el, s) => {
          s.root?.update({ _installableModal: null })
        }
      }
    }
  }
}
